import React, { useState, useMemo, useRef, useEffect } from "react";
import styled from "styled-components";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableHead from "./TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import provideUserFile from "common/ProvideFile";
import { WrapperToolbarButton, ToolbarButton, SearchWrapper } from "./styles";
import TableFilter from "./TableFilter";
import ExpandableCell from "./TableExpandableCell";
import TableNavigation from "./TablePagination";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  getComparator,
  defaultLabelDisplayedRows,
  getFilteredData,
  createCVSFilename,
  getCellClass,
  escapeCsvCell,
} from "./utils";
import ActionCell from "./ActionCell";
import SelectAllButton from "./SelectAllButton";

const Wrapper = styled.div`
  width: 100%;
  overflow: hidden;
  padding: 5px 0 25px;

  .MuiTableContainer-root {
    border: 1px solid var(--table-border-color);
    border-radius: 5px;
    border-collapse: inherit;
    font-family: "AllianceNo2-Regular";
    color: var(--Text-Primary) !important;
    overflow-y: hidden;
    .MuiTableCell-root {
      font-size: 12px;
      line-height: 1.4;
      color: var(--Text-Primary) !important;
      border-bottom: none;
      padding: 8px 6px 8px 6px;
      &.selectTableCell {
        padding: 1px 3px 1px 3px;

        svg path {
          stroke: var(--Text-Primary);
          fill: var(--Text-Primary);
        }
      }
    }
    .MuiTableCell-root.grow-width {
      width: 100%;
    }
    th.MuiTableCell-root {
      white-space: nowrap;
      background-color: var(--table-header-background-color);
      border-bottom: 1px solid var(--table-border-color);
      vertical-align: middle;
      color: var(--text-primary);
      font-family: "GT-america-mono" !important;
      font-size: var(--font-size, 12px);
      font-weight: 400;
      .MuiTableSortLabel-root {
        .MuiSvgIcon-root {
          margin-right: 2px;
          margin-left: 2px;
          font-size: 14px;
        }
      }
      span,
      div {
        font-family: "GT-america-mono" !important;
        font-size: var(--font-size, 12px);
        font-weight: 400;

        &:hover {
          color: var(--subtitle-color);

          .MuiTableSortLabel-iconDirectionDesc,
          .MuiTableSortLabel-iconDirectionAsc {
            path {
              stroke: var(--subtitle-color);
              fill: var(--subtitle-color);
            }
          }
        }

        &.Mui-active {
          color: var(--subtitle-color);

          .MuiTableSortLabel-iconDirectionDesc,
          .MuiTableSortLabel-iconDirectionAsc {
            path {
              stroke: var(--subtitle-color);
              fill: var(--subtitle-color);
            }
          }
        }
      }
      .MuiCheckbox-root {
        input {
          margin: 0 !important;
        }
        svg path {
          stroke: transparent;
          fill: var(--Text-Primary);
        }
      }
    }
    tbody.MuiTableBody-root {
      tr.MuiTableRow-root {
        vertical-align: top;
      }
      tr.MuiTableRow-root:nth-of-type(odd) {
        background-color: var(--table-striped-background-color);
      }
    }
    tr.MuiTableRow-root {
      &.not-selectable {
        &:hover {
          cursor: default;
        }
      }
      td {
        &.hyperlink-text {
          color: var(--link-color) !important;
        }
        &.hyperlink-text,
        &.hasClick {
          &:hover {
            cursor: pointer;
          }
        }
        &:hover {
          cursor: default;
        }
        .MuiCheckbox-root {
          input {
            margin: 0 !important;
          }
          svg {
            margin-left: 4px;
            path {
              stroke: transparent;
              fill: var(--Text-Primary);
            }
          }
        }
      }
    }
    .MuiCollapse-wrapper {
      tr {
        background: none !important;
      }
    }
  }
`;

const Toolbar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
  .toolbarPagination {
    display: flex;
    overflow: initial;
    padding-right: 24px;
    .MuiTablePagination-toolbar::after {
      content: "entries";
      display: inline-block;
      width: 30px;
      height: 12px;
      font-family: "AllianceNo2-SemiBold";
      color: var(--Text-Primary);
      font-weight: 100 !important;
      font-size: var(--font-size, 12px);
      margin-top: -5px;
      margin-left: -25px;
    }
    & > div {
      min-height: 40px !important;
      .MuiTablePagination-selectLabel {
        font-family: "AllianceNo2-SemiBold";
        color: var(--Text-Primary);
        font-weight: 100 !important;
        font-size: var(--font-size, 12px);
        margin: 0 -5px 0 -20px;
      }
    }
    .MuiTablePagination-selectIcon {
      path {
        stroke: var(--Text-Primary);
        fill: var(--Text-Primary);
      }
    }
    .MuiTablePagination-actions,
    .MuiTablePagination-displayedRows {
      display: none;
    }
  }
  @media (max-width: 650px) {
    display: block;
    width: 100%;
    & > div:first-child {
      justify-content: flex-start;
    }
    & > div:last-child {
      justify-content: flex-end;
    }
  }
`;

const ToolbarColumn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Footer = styled.div`
  margin-top: -10px;
`;

const NrSelected = styled.div`
  font-family: "AllianceNo2-SemiBold" !important;
  font-weight: 100 !important;
  font-size: 12px !important;
  color: var(--Text-Primary) !important;
  margin-left: 10px;
`;

const SelectAllButtons = styled.div`
  margin-left: -10px;
  padding: 0;

  button {
    margin: 0 10px !important;
    padding: 10px 0px !important;

    &:hover {
      background: none !important;
    }
  }
`;

const SelectButtonWrapper = styled.button`
  display: flex;

  &:hover {
    background: none !important;
  }
  svg {
    margin-left: 3px;
  }
`;

function SelectButton({ count, onDelete }) {
  if (count === 0 || !onDelete) {
    return <NrSelected>{count} Selected</NrSelected>;
  }
  return (
    <SelectButtonWrapper
      type="button"
      onClick={onDelete}
      className={`btn btn-link`}
    >
      {count} Selected {onDelete && <DeleteIcon />}
    </SelectButtonWrapper>
  );
}

export default function MuiTable({
  hasPagination = true,
  hasSelectNrRows = true,
  isSelectable = false,
  isRowSelectable = true,
  hasColumnFilter = true,
  data = [],
  sort = true,
  search = true,
  exportCSV = true,
  columns = [],
  selected = [],
  setSelected,
  deleteSelected,
  csvPrefix = "export",
  rowsPerPageNr = 10,
  collapsed = true,
  hasExtraSelect = false,
  orderDefault = "asc",
  orderByDefault = "",
  orderDefaultType = "",
  className = "",
}) {
  const [order, setOrder] = useState(orderDefault);
  const [orderBy, setOrderBy] = useState(orderByDefault);
  const [orderType, setOrderType] = useState(orderDefaultType);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageNr);
  const [searchQuery, setSearchQuery] = useState("");
  const fnParse = useRef(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [filters, setFilters] = useState([]);
  const [logicalOperator, setLogicalOperator] = useState("AND");
  const filteredData = getFilteredData({
    data,
    searchQuery,
    columns,
    filters,
    logicalOperator,
  });

  useEffect(() => {
    setOrderType(orderDefaultType);
  }, [orderDefaultType]);

  useEffect(() => {
    setOrderBy(orderByDefault);
  }, [orderByDefault]);

  useEffect(() => {
    setOrder(orderDefault);
  }, [orderDefault]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredData.length) : 0;

  const { orderedData, visibleRows } = useMemo(() => {
    const orderedData = [...filteredData].sort(
      getComparator(order, orderBy, orderType, fnParse),
    );
    const visibleRows = orderedData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
    return { orderedData, visibleRows };
  }, [order, orderBy, page, rowsPerPage, filteredData, orderType, fnParse]);

  useEffect(() => {
    if (setSelected) {
      setSelected((prevSelected) => {
        return orderedData.reduce((acc, item) => {
          if (prevSelected.includes(item.id)) {
            acc.push(item.id);
          }
          return acc;
        }, []);
      });
    }
  }, [JSON.stringify(orderedData)]);

  useEffect(() => {
    setPage(0);
  }, [filteredData.length]);

  const allVisibleSelected = useMemo(() => {
    return (
      visibleRows.length > 0 &&
      visibleRows.every((row) => selected.includes(row.id))
    );
  }, [visibleRows, selected]);

  const handleRequestSort = (event, property, orderType, parse) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
    setOrderType(orderType);
    fnParse.current = parse;
    if (data.length > rowsPerPageNr) {
      setPage(0);
    }
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    const orderedSelection = orderedData.reduce((acc, item) => {
      if (newSelected.includes(item.id)) {
        acc.push(item.id);
      }
      return acc;
    }, []);
    setSelected(orderedSelection);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchInput = ({ target }) => {
    setSearchQuery(target.value);
  };

  const norHiddenOrActions = ({ hidden = false, actions = false }) =>
    hidden !== true && actions === false;

  const handleCSVDownload = () => {
    if (typeof exportCSV === "function") {
      provideUserFile(createCVSFilename(csvPrefix), exportCSV());
    } else {
      const headArray = columns
        .filter((c) => !c.hidden && c.id !== "ACTIONS")
        .map((c) => escapeCsvCell(c.label));

      const dataToExport = [...filteredData].sort(
        getComparator(order, orderBy, orderType, fnParse),
      );
      const rowArrays = dataToExport.map((item) => {
        return columns
          .filter((c) => !c.hidden && c.id !== "ACTIONS")
          .map((column) => {
            const value =
              item[column.id] !== undefined ? item[column.id] : "n/a";
            return escapeCsvCell(value);
          })
          .join(",");
      });

      const content = [headArray.join(","), ...rowArrays].join("\n");
      provideUserFile(createCVSFilename(csvPrefix), content);
    }
  };

  const handleSelectAllClick = (event) => {
    if (allVisibleSelected) {
      setSelected([]);
    } else {
      setSelected(visibleRows.map((n) => n.id));
    }
  };

  const handleSelectAllFiltered = () => {
    setSelected(filteredData.map((n) => n.id));
  };

  const isAllSelected =
    selected.length === filteredData.length && filteredData.length > 0;

  return (
    <Wrapper className={className}>
      {hasExtraSelect ? (
        <SelectAllButtons>
          <button
            className="btn btn-link waves-effect"
            onClick={() => {
              const newSelected = visibleRows.map((n) => n.id);
              setSelected(newSelected);
            }}
          >
            Select all to plot
          </button>
          <button
            onClick={() => setSelected([])}
            className="btn btn-link waves-effect"
          >
            Deselect all to plot
          </button>
        </SelectAllButtons>
      ) : null}
      <Toolbar className="toolbar">
        <ToolbarColumn className="toolbar-left">
          {hasSelectNrRows ? (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              labelRowsPerPage={"Show"}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              className="toolbarPagination"
            />
          ) : null}
          {isSelectable ? (
            <SelectButton
              count={selected.length}
              onDelete={
                deleteSelected ? () => deleteSelected(selected) : undefined
              }
            />
          ) : null}
        </ToolbarColumn>
        <ToolbarColumn className="toolbar-right">
          {exportCSV ? (
            <WrapperToolbarButton>
              <ToolbarButton onClick={handleCSVDownload}>
                <span>Export:</span>
                <i className="material-icons">file_download</i>
              </ToolbarButton>
            </WrapperToolbarButton>
          ) : null}
          {hasColumnFilter ? (
            <TableFilter
              columns={columns.filter(norHiddenOrActions)}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              filters={filters}
              setFilters={setFilters}
              logicalOperator={logicalOperator}
              setLogicalOperator={setLogicalOperator}
            />
          ) : null}
          {search ? (
            <SearchWrapper>
              <label htmlFor="table-search">Search:</label>
              <input
                type="search"
                id="table-search"
                name="q"
                onChange={handleSearchInput}
              />
            </SearchWrapper>
          ) : null}
        </ToolbarColumn>
      </Toolbar>
      {isSelectable &&
      allVisibleSelected &&
      filteredData.length > visibleRows.length ? (
        <SelectAllButton
          isAllSelected={isAllSelected}
          onSelectAll={handleSelectAllFiltered}
          onClearSelection={() => setSelected([])}
          totalCount={filteredData.length}
        />
      ) : null}
      <TableContainer>
        <Table
          sx={{ maxHeight: 750 }}
          aria-labelledby="tableTitle"
          size={"medium"}
          className="muitable muidataTable"
        >
          <TableHead
            columns={columns}
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={filteredData.length}
            isSelectable={isSelectable}
            sort={sort}
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            filters={filters}
          />
          <TableBody>
            {visibleRows.map((row, index) => {
              const isItemSelected = selected.includes(row.id);
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <TableRow
                  hover
                  onClick={
                    isRowSelectable
                      ? (event) => handleClick(event, row.id)
                      : undefined
                  }
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={`${row.id}-${index}`}
                  selected={isItemSelected}
                  sx={{ cursor: "pointer" }}
                  className={isRowSelectable ? "" : "not-selectable"}
                >
                  {isSelectable ? (
                    <TableCell
                      className="selectTableCell"
                      key={`cell-selectable-${index}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        inputProps={{
                          "aria-labelledby": labelId,
                        }}
                        onClick={(event) => handleClick(event, row.id)}
                      />
                    </TableCell>
                  ) : null}
                  {columns.map((column) => {
                    const {
                      id,
                      numeric,
                      labelId: colLabelId,
                      renderCell,
                      cellClassName,
                      onClick,
                      hidden,
                      expandable,
                      expandColumn,
                      expandColumns,
                    } = column;
                    if (hidden || expandColumn) {
                      return null;
                    }
                    if (expandable) {
                      return (
                        <ExpandableCell
                          value={row[id]}
                          key={`cell${index}-${column.id}-${row.id}`}
                          columns={expandColumns}
                          collapsed={collapsed}
                          numeric={numeric}
                        />
                      );
                    }
                    if (id === "ACTIONS") {
                      return (
                        <ActionCell
                          actions={column.actions}
                          row={row}
                          key={`action${index}-${row.id}`}
                        />
                      );
                    }
                    const cellId = colLabelId ? labelId : null;
                    const cellClass = getCellClass(
                      cellClassName,
                      row,
                      column,
                      row[id],
                    );
                    const restProps = {
                      ...(cellId && { id: cellId }),
                      ...(cellClass && { className: cellClass }),
                      ...(onClick && { onClick: () => onClick(row[id], row) }),
                    };
                    return (
                      <TableCell
                        key={`cell${index}-${column.id}-${row.id}`}
                        align={numeric ? "right" : "left"}
                        style={
                          sort
                            ? { padding: "8px 30px 8px 3px" }
                            : { padding: "8px 3px 8px 3px" }
                        }
                        {...restProps}
                      >
                        {renderCell && typeof renderCell === "function"
                          ? renderCell(row[id], row)
                          : row[id]}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                key="empty-rows"
                // style={{
                //   height: 37 * emptyRows,
                // }}
              >
                <TableCell
                  colSpan={
                    columns.filter((c) => !c.hidden).length +
                    (isSelectable ? 1 : 0)
                  }
                />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {hasPagination ? (
        <Footer>
          <TableNavigation
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            labelRowsPerPage={""}
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            className="footerPagination"
            labelDisplayedRows={defaultLabelDisplayedRows}
            showFirstButton={true}
            showLastButton={true}
          />
        </Footer>
      ) : null}
    </Wrapper>
  );
}
